import request from '@/utils/request'
import SpacePermissions from '@/enum/Permissions'
import type { AxiosResponse } from 'axios'
import type { LaravelCollection, LaravelResource, LaravelPaginated } from '../types'
import type { Space, SpaceListItem } from '@/models/Space'

interface ListQueryArg {
  page?: number | null
  limit?: number | null
  order?: string | null
}

interface TeamListRequest {
  search?: string | null
  listQuery?: ListQueryArg | null
}

/** GET /api/v1/spaces */
export async function getAllSpaces(
  data?: TeamListRequest | null,
): Promise<AxiosResponse<LaravelPaginated<Space>>> {
  const params: string[] = []
  if (data) {
    const { search, listQuery } = data
    if (listQuery?.page) params.push('page=' + listQuery.page)
    if (listQuery?.limit) params.push('limit=' + listQuery.limit)
    if (listQuery?.order) params.push('order=' + listQuery.order)
    if (search) params.push('search=' + search)
  }
  return request({
    url: '/api/v1/spaces?' + params.join('&'),
    method: 'get',
  })
}

export interface SettingsSpaceListQuery {
  search?: string | null
  /** Include archived teams (the settings are where they are restored). */
  withArchived?: boolean
  /** Only teams the caller administers (managers: all teams). */
  manageOnly?: boolean
  page?: number | null
  /** Honoured by the backend from MR 2a on; older backends answer 15 per page. */
  limit?: number | null
}

export interface MoveContentTypeGroup {
  /** null when the content carries no type at all. */
  value: string | null
  /** null when the type no longer exists, or never belonged to this team. */
  title: string | null
  /** Taken from the content, not from the type — an edited prefix leaves both in the data. */
  prefixes: string[]
  count: number
}

export interface MoveContentPreview {
  tasks: { total: number; types: MoveContentTypeGroup[] }
  protocols: { total: number; types: MoveContentTypeGroup[] }
  time_entries: { total: number; stays_with_source: boolean }
}

export interface MoveContentMappingEntry {
  source_type: string | null
  target_type_id?: string | null
  new_type?: { title: string; prefix: string }
}

export interface MoveContentPayload {
  target_space_id: string
  task_types: MoveContentMappingEntry[]
  protocol_types: MoveContentMappingEntry[]
}

/**
 * Space administration requests under `base`: `/api/v1` for the own account, the admin account
 * prefix for a super admin managing a customer account (#290).
 */
export function spaceRequests(base = '/api/v1') {
  const spaces = base + '/spaces'

  /**
   * GET {base}/spaces for the settings: never with `with_portals` — contact portals are managed on
   * the contact, not in the team list.
   */
  function getSettingsSpaces(
    query: SettingsSpaceListQuery = {},
  ): Promise<AxiosResponse<LaravelPaginated<SpaceListItem>>> {
    const params: Record<string, string | number> = {}
    if (query.manageOnly) params['filter[permissions]'] = SpacePermissions.ADMIN
    if (query.withArchived) params.with_archived = 'true'
    if (query.search) params.search = query.search
    if (query.page) params.page = query.page
    if (query.limit) params.limit = query.limit
    return request({ url: spaces, method: 'get', params })
  }

  /**
   * All teams of a settings list in one array. Asks for a large page and follows the pagination,
   * so it also works against a backend that still ignores `limit`. Portal teams are dropped.
   */
  async function getAllSettingsSpaces(
    query: Omit<SettingsSpaceListQuery, 'page' | 'limit'> = {},
  ): Promise<SpaceListItem[]> {
    const all: SpaceListItem[] = []
    let page = 1
    // A hard stop keeps a misbehaving pagination from looping forever.
    for (let guard = 0; guard < 50; guard++) {
      const resp = await getSettingsSpaces({ ...query, page, limit: 500 })
      all.push(...resp.data.data)
      const meta = resp.data.meta
      if (!meta || meta.current_page >= meta.last_page) break
      page = meta.current_page + 1
    }
    return all.filter((team) => !team.portal)
  }

  /**
   * GET {base}/spaces/{id}/permissions — rights of every member and read-only member at once:
   * `{ data: { <user_id>: [permission, …] } }`.
   */
  function getSpacePermissionsBulk(
    space_id: string,
  ): Promise<AxiosResponse<{ data: Record<string, string[]> }>> {
    return request({
      url: spaces + '/' + space_id + '/permissions',
      method: 'get',
    })
  }

  /**
   * Rights of the given people in a team: the bulk endpoint when the backend has it (MR 2a),
   * otherwise one request per person as before.
   */
  async function loadSpacePermissions(
    space_id: string,
    user_ids: string[],
  ): Promise<Record<string, string[]>> {
    try {
      const resp = await getSpacePermissionsBulk(space_id)
      const data = resp.data?.data
      if (data && typeof data === 'object' && !Array.isArray(data)) return data
    } catch (err: any) {
      const status = err?.response?.status
      // Only a missing endpoint falls back; a 403 is an answer about the team.
      if (status !== 404 && status !== 405) throw err
    }
    // One failing person (e.g. a deleted user) must not take everybody else's rights with it.
    const results = await Promise.allSettled(user_ids.map((user_id) => getPermissions(space_id, user_id)))
    return Object.fromEntries(
      user_ids.map((user_id, index) => {
        const result = results[index]
        const permissions = result.status === 'fulfilled' ? (result.value.data as unknown as string[]) : []
        return [user_id, Array.isArray(permissions) ? permissions : []]
      }),
    )
  }

  /** GET {base}/spaces/{id}/move-content/preview */
  function previewContentMove(
    id: string,
  ): Promise<AxiosResponse<{ data: MoveContentPreview }>> {
    return request({
      url: spaces + '/' + id + '/move-content/preview',
      method: 'get',
    })
  }

  /** POST {base}/spaces/{id}/move-content */
  function moveContent(
    id: string,
    payload: MoveContentPayload,
  ): Promise<AxiosResponse<{ data: { tasks: number; protocols: number } }>> {
    return request({
      url: spaces + '/' + id + '/move-content',
      method: 'post',
      data: payload,
    })
  }

  /** POST {base}/spaces/{id}/archive */
  function archiveSpace(id: string): Promise<AxiosResponse<LaravelResource<Space>>> {
    return request({
      url: spaces + '/' + id + '/archive',
      method: 'post',
    })
  }

  /** DELETE {base}/spaces/{id}/archive */
  function unarchiveSpace(id: string): Promise<AxiosResponse<LaravelResource<Space>>> {
    return request({
      url: spaces + '/' + id + '/archive',
      method: 'delete',
    })
  }

  /** GET {base}/spaces/{id} */
  function getSpaceById(id: string): Promise<AxiosResponse<LaravelResource<Space>>> {
    return request({
      url: spaces + '/' + id,
      method: 'get',
    })
  }

  /** DELETE {base}/spaces/{id} */
  function deleteSpace(id: string): Promise<AxiosResponse<void>> {
    return request({
      url: spaces + '/' + id,
      method: 'delete',
    })
  }

  /** POST {base}/spaces/{space_id}/members */
  function addMemberToSpace(
    space_id: string,
    user_id: string,
  ): Promise<AxiosResponse<LaravelResource<Space>>> {
    return request({
      url: spaces + '/' + space_id + '/members',
      method: 'post',
      data: { user_id },
    })
  }

  /** POST {base}/spaces/{space_id}/readonly_members */
  function addReadonlyMemberToSpace(
    space_id: string,
    user_id: string,
  ): Promise<AxiosResponse<LaravelResource<Space>>> {
    return request({
      url: spaces + '/' + space_id + '/readonly_members',
      method: 'post',
      data: { user_id },
    })
  }

  /** DELETE {base}/spaces/{space_id}/members/{user_id} */
  function deleteMemberFromSpace(
    space_id: string,
    user_id: string,
  ): Promise<AxiosResponse<void>> {
    return request({
      url: spaces + '/' + space_id + '/members/' + user_id,
      method: 'delete',
    })
  }

  /** DELETE {base}/spaces/{space_id}/readonly_members/{user_id} */
  function deleteReadonlyMemberFromSpace(
    space_id: string,
    user_id: string,
  ): Promise<AxiosResponse<void>> {
    return request({
      url: spaces + '/' + space_id + '/readonly_members/' + user_id,
      method: 'delete',
    })
  }

  /** GET {base}/spaces/{space_id}/members/{user_id}/permissions */
  function getPermissions(
    space_id: string,
    user_id: string,
  ): Promise<AxiosResponse<LaravelCollection<{ space_id: string; permissions: string[] }>>> {
    return request({
      url: spaces + '/' + space_id + '/members/' + user_id + '/permissions',
      method: 'get',
    })
  }

  /** POST {base}/spaces/{space_id}/members/{user_id}/permissions */
  function updatePermissions(
    space_id: string,
    user_id: string,
    permissions: string[],
  ): Promise<AxiosResponse<void>> {
    return request({
      url: spaces + '/' + space_id + '/members/' + user_id + '/permissions',
      method: 'post',
      data: { permissions },
    })
  }

  /** POST {base}/spaces/{space_id}/readonly_members/{user_id}/permissions */
  function updateReadonlyPermissions(
    space_id: string,
    user_id: string,
    permissions: string[],
  ): Promise<AxiosResponse<void>> {
    return request({
      url: spaces + '/' + space_id + '/readonly_members/' + user_id + '/permissions',
      method: 'post',
      data: { permissions },
    })
  }

  /** PUT {base}/spaces/{id} */
  function updateSpace(
    id: string,
    data: { name?: string; allow_non_member_create?: boolean; hidden?: boolean; short?: string | null; color?: string },
  ): Promise<AxiosResponse<LaravelResource<Space>>> {
    const payload: Record<string, unknown> = {}
    if (data.name !== undefined) payload.name = data.name
    if (data.allow_non_member_create !== undefined) payload.allow_non_member_create = data.allow_non_member_create
    if (data.hidden !== undefined) payload.hidden = data.hidden
    // null is sent on purpose: it clears the code and the initials are used again.
    if (data.short !== undefined) payload.short = data.short
    if (data.color !== undefined) payload.color = data.color
    return request({
      url: spaces + '/' + id,
      method: 'put',
      data: payload,
    })
  }

  /** POST {base}/spaces/{id}/logo (multipart, png/jpeg/webp up to 1 MB) */
  function uploadSpaceLogo(id: string, file: File): Promise<AxiosResponse<LaravelResource<Space>>> {
    const data = new FormData()
    data.append('file', file)
    return request({
      url: spaces + '/' + id + '/logo',
      method: 'post',
      data,
    })
  }

  /** DELETE {base}/spaces/{id}/logo */
  function deleteSpaceLogo(id: string): Promise<AxiosResponse<LaravelResource<Space>>> {
    return request({
      url: spaces + '/' + id + '/logo',
      method: 'delete',
    })
  }

  return {
    getSettingsSpaces,
    getAllSettingsSpaces,
    getSpacePermissionsBulk,
    loadSpacePermissions,
    previewContentMove,
    moveContent,
    archiveSpace,
    unarchiveSpace,
    getSpaceById,
    deleteSpace,
    addMemberToSpace,
    addReadonlyMemberToSpace,
    deleteMemberFromSpace,
    deleteReadonlyMemberFromSpace,
    getPermissions,
    updatePermissions,
    updateReadonlyPermissions,
    updateSpace,
    uploadSpaceLogo,
    deleteSpaceLogo,
  }
}

const v1 = spaceRequests()
export const getSettingsSpaces = v1.getSettingsSpaces
export const getAllSettingsSpaces = v1.getAllSettingsSpaces
export const getSpacePermissionsBulk = v1.getSpacePermissionsBulk
export const loadSpacePermissions = v1.loadSpacePermissions
export const previewContentMove = v1.previewContentMove
export const moveContent = v1.moveContent
export const archiveSpace = v1.archiveSpace
export const unarchiveSpace = v1.unarchiveSpace
export const getSpaceById = v1.getSpaceById
export const deleteSpace = v1.deleteSpace
export const addMemberToSpace = v1.addMemberToSpace
export const addReadonlyMemberToSpace = v1.addReadonlyMemberToSpace
export const deleteMemberFromSpace = v1.deleteMemberFromSpace
export const deleteReadonlyMemberFromSpace = v1.deleteReadonlyMemberFromSpace
export const getPermissions = v1.getPermissions
export const updatePermissions = v1.updatePermissions
export const updateReadonlyPermissions = v1.updateReadonlyPermissions
export const updateSpace = v1.updateSpace
export const uploadSpaceLogo = v1.uploadSpaceLogo
export const deleteSpaceLogo = v1.deleteSpaceLogo

/** POST /api/v1/account_manager/spaces */
export function createSpace(
  data: { name: string },
): Promise<AxiosResponse<LaravelResource<Space>>> {
  return request({
    url: '/api/v1/account_manager/spaces',
    method: 'post',
    data: { name: data.name },
  })
}